import React from "react";
import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";

import SalesFieldRenderer from "../SalesFieldRenderer/SalesFieldRenderer";

/**
 * Editor for a section's repeatable `content.items[]` rows. Each item carries
 * its own `order` (recomputed from array position on save). Supports add,
 * remove, reorder, and per-field editing driven by `itemFields`.
 */
const ItemsEditor = ({ itemFields, items, page, disabled, onChange }) => {
  const list = Array.isArray(items) ? items : [];

  const buildEmptyItem = () => {
    const obj = {};
    itemFields.forEach((f) => {
      obj[f.key] = "";
    });
    return obj;
  };

  const updateItem = (index, key, value) => {
    const next = list.map((it, i) =>
      i === index ? { ...it, [key]: value } : it
    );
    onChange(next);
  };

  const addItem = () => onChange([...list, buildEmptyItem()]);

  const removeItem = (index) =>
    onChange(list.filter((_, i) => i !== index));

  const moveItem = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= list.length) return;
    const next = [...list];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={sectionLabelStyles}>Items</Typography>
      {list.length === 0 ? (
        <Typography sx={emptyStyles}>No items yet.</Typography>
      ) : null}
      {list.map((item, index) => (
        <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography sx={itemTitleStyles}>Item {index + 1}</Typography>
            {!disabled ? (
              <Box>
                <IconButton
                  size="small"
                  disabled={index === 0}
                  onClick={() => moveItem(index, -1)}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={index === list.length - 1}
                  onClick={() => moveItem(index, 1)}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => removeItem(index)}
                  sx={{ color: "#B00020" }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : null}
          </Box>
          {itemFields.map((field) => (
            <SalesFieldRenderer
              key={field.key}
              field={field}
              value={item[field.key]}
              page={page}
              disabled={disabled}
              onChange={(v) => updateItem(index, field.key, v)}
            />
          ))}
        </Paper>
      ))}
      {!disabled ? (
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addItem}
          style={addButtonStyles}
        >
          Add Item
        </Button>
      ) : null}
    </Box>
  );
};

export default ItemsEditor;

const sectionLabelStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 700,
  fontSize: 16,
  color: "#020303",
  mb: 1.5,
};

const itemTitleStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 600,
  fontSize: 14,
  color: "#020303",
};

const emptyStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 13,
  color: "#808080",
  mb: 1,
};

const addButtonStyles = {
  textTransform: "none",
  color: "#00A7CD",
  borderColor: "#00A7CD",
  fontFamily: "Avenir Book, sans-serif",
};
